import React from "react";

const SECTION = { marginBottom: 20 };
const SECTION_TITLE = { fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 };
const DIVIDER = { borderTop: "1px solid #1e2d45", margin: "16px 0" };
const SUB_LABEL = { fontSize: 10, color: "#8a9bb0", marginBottom: 6 };

function NumInput({ label, value, onChange, max = 6 }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 3 }}>{label}</div>
      <input
        type="number" min="0" max={max} value={value}
        onChange={e => onChange(Math.max(0, Math.min(max, parseInt(e.target.value, 10) || 0)))}
        style={{
          width: "100%", textAlign: "center",
          background: "#0f172a", border: "1px solid #334155",
          color: "#e2e8f0", borderRadius: 5,
          padding: "4px 0", fontSize: 12, outline: "none",
        }}
      />
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, cursor: "pointer", userSelect: "none" }}
      onClick={() => onChange(!value)}
    >
      <div style={{
        width: 34, height: 18, borderRadius: 10, flexShrink: 0,
        background: value ? "#2563eb" : "#334155",
        position: "relative", transition: "background 0.2s",
      }}>
        <div style={{
          position: "absolute", top: 2,
          left: value ? 18 : 2, width: 14, height: 14,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s",
        }} />
      </div>
      <span style={{ fontSize: 12, color: value ? "#93c5fd" : "#8a9bb0" }}>{label}</span>
    </div>
  );
}

export default function RunTableSettingsModal({ open, settings, onChange, onClose, hasWeights }) {
  if (!open) return null;

  const set = (key, val) => onChange({ ...settings, [key]: val });

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#0f172a", border: "1px solid #1e2d45",
        borderRadius: 12, width: 420, maxWidth: "95vw",
        maxHeight: "88vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        padding: "24px 28px",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>Run Tables Settings</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Formatting & display options</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#64748b", fontSize: 18, cursor: "pointer", padding: "2px 6px" }}
          >×</button>
        </div>

        <div style={DIVIDER} />

        {/* ── Decimal Places ────────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Decimal Places</div>

          <div style={SUB_LABEL}>General</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            <NumInput label="Frequency" value={settings.freqDecimals} onChange={v => set("freqDecimals", v)} />
            <NumInput label="Percent (%)" value={settings.pctDecimals} onChange={v => set("pctDecimals", v)} />
            <NumInput label="Base" value={settings.baseDecimals} onChange={v => set("baseDecimals", v)} />
          </div>

          <div style={SUB_LABEL}>Statistics</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            <NumInput label="Mean" value={settings.meanDecimals} onChange={v => set("meanDecimals", v)} />
            <NumInput label="Median" value={settings.medianDecimals} onChange={v => set("medianDecimals", v)} />
            <NumInput label="Std Dev" value={settings.stdDecimals} onChange={v => set("stdDecimals", v)} />
            <NumInput label="Std Error" value={settings.seDecimals} onChange={v => set("seDecimals", v)} />
          </div>

          <div style={SUB_LABEL}>Cell Value Precision (stored in cell)</div>
          <div style={{ fontSize: 10, color: "#475569", marginBottom: 8 }}>
            Controls the exact value written into each Excel cell (visible in formula bar). Independent of display decimals above.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <NumInput label="Frequency" value={settings.cellFreqDecimals} onChange={v => set("cellFreqDecimals", v)} max={10} />
            <NumInput label="Percent (%)" value={settings.cellPctDecimals} onChange={v => set("cellPctDecimals", v)} max={10} />
            <NumInput label="Base" value={settings.cellBaseDecimals} onChange={v => set("cellBaseDecimals", v)} max={10} />
          </div>
        </div>

        <div style={DIVIDER} />

        {/* ── Base Row Display ──────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Base Rows</div>
          <Toggle label="Show Unweighted Base" value={settings.showUnweightedBase} onChange={v => set("showUnweightedBase", v)} />
          {hasWeights && (
            <Toggle label="Show Weighted Base" value={settings.showWeightedBase} onChange={v => set("showWeightedBase", v)} />
          )}
          <Toggle label="Show Base %" value={settings.showBasePct} onChange={v => set("showBasePct", v)} />
        </div>

        <div style={DIVIDER} />

        {/* ── Global Row Filter ─────────────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Global Row Filter</div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
            Enter filter logic separated by <strong style={{ color: "#93c5fd" }}>;</strong> for multiple outputs.
            Leave blank to export all respondents (ALL).
          </div>
          <input
            type="text"
            placeholder="e.g. S1=1;S1=2  or  ALL;S1=1"
            value={settings.globalFilter || ''}
            onChange={e => set("globalFilter", e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#0f172a", border: "1px solid #334155",
              color: "#e2e8f0", borderRadius: 5,
              padding: "6px 10px", fontSize: 12, outline: "none",
            }}
          />
          <div style={{ fontSize: 10, color: "#475569", marginTop: 5, marginBottom: 12 }}>
            Each term creates a separate sheet with its own Table of Contents.
          </div>
          <Toggle
            label="Separate Excel file per filter"
            value={settings.separateExcelPerFilter || false}
            onChange={v => set("separateExcelPerFilter", v)}
          />
          {settings.separateExcelPerFilter && (
            <div style={{ fontSize: 10, color: "#475569", marginTop: -4 }}>
              Multiple Excel files will be zipped and downloaded together.
            </div>
          )}
        </div>

        <div style={DIVIDER} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              background: "linear-gradient(135deg,#1d4ed8,#2563eb)",
              color: "#fff", border: "none", borderRadius: 7,
              padding: "8px 22px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
